import { Button, Icon, Select } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ORGANIZATION_PAYMENT_TYPE, PAYMENT_METHOD_TYPES } from '@hero/constants';
import { useIsAdminOrganization, useSelectedOrganization } from '@hero/hooks';
import { OnboardingCard } from '@hero/molecules';
import { PaymentMethodMoreInformationModal } from '@hero/organisms';
import { useGetPaymentMethods, useGetProviderUrl } from '@hero/services/organization-service/hooks';
import { useNotification, useOnboardingStore } from '@hero/store/hooks';
import { addPaymentMethodLink, editPaymentMethodLink } from '@hero/utils';
import { openPopUp } from '@hero/utils/openPopup';

import { CustomOption } from './CustomOption';
import * as S from './style';

const PaymentMethodInput = ({
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  featureType,
  onTourButtonClick
}) => {
  const { showSuccess } = useNotification();
  const { data: providerUrl } = useGetProviderUrl();
  const { t } = useTranslation();
  const onboarding = useOnboardingStore();
  const { data: { paymentMethods = [] } = {}, refetch } = useGetPaymentMethods();
  const organization = useSelectedOrganization();
  const isOrganizationAdmin = useIsAdminOrganization();

  const [isOnboardingCardPaymentShown, setIsOnboardingCardPaymentShown] = useState(false);
  const [showMoreInformationModal, setShowMoreInformationModal] = useState(false);

  const isMultiCCEnabled =
    organization?.configs?.accountPaymentType === ORGANIZATION_PAYMENT_TYPE.multi_cc;

  const getPaymentOption = paymentMethod => {
    if (!paymentMethod) {
      return null;
    }

    return {
      label:
        paymentMethod?.type === PAYMENT_METHOD_TYPES.houseAccount
          ? t('invoiceAccount')
          : paymentMethod?.name,
      value: paymentMethod?.code
    };
  };

  const options = useMemo(() => {
    return paymentMethods.map(paymentMethod => getPaymentOption(paymentMethod));
  }, [paymentMethods]);

  const isHouseAccount = useMemo(() => {
    return !!paymentMethods.find(
      paymentMethod => paymentMethod.type === PAYMENT_METHOD_TYPES.houseAccount
    );
  }, [paymentMethods]);

  useEffect(() => {
    setTimeout(() => {
      setIsOnboardingCardPaymentShown(true);
    }, 1000);
  }, []);

  useEffect(() => {
    if (isHouseAccount && !selectedPaymentMethod) {
      setSelectedPaymentMethod(
        paymentMethods.find(
          paymentMethod => paymentMethod.type === PAYMENT_METHOD_TYPES.houseAccount
        )
      );
    }
  }, [isHouseAccount, selectedPaymentMethod]);

  const infoCardContent = (
    <Button color="neutral" variant="link" onClick={() => setShowMoreInformationModal(true)}>
      {t('moreInformation')}
    </Button>
  );

  return (
    <S.PaymentMethodInput>
      <OnboardingCard
        styles={{
          wrapper: {
            right: 40,
            top: -12
          }
        }}
        position="right-start"
        cardOffset={[12, 0]}
        isCardShown={isOnboardingCardPaymentShown}
        setIsCardShown={setIsOnboardingCardPaymentShown}
        title={t('selectPaymentMethod')}
        description={t('selectPaymentMethodDesc')}
        content={infoCardContent}
        onTourButtonClick={onTourButtonClick}
        isInfoActive={onboarding?.[featureType]?.infoCardsActive}
        featureType={featureType}
      />
      <PaymentMethodMoreInformationModal
        isModalOpen={showMoreInformationModal}
        onRequestClose={() => setShowMoreInformationModal(false)}
      />

      {paymentMethods.length > 0 ? (
        <>
          <Select
            value={getPaymentOption(selectedPaymentMethod)}
            options={options}
            onChange={e => {
              setSelectedPaymentMethod(
                paymentMethods.find(paymentMethod => paymentMethod.code === e.value)
              );
            }}
            placeholder={t('selectPaymentMethod')}
            disabled={isHouseAccount}
            components={{ Option: CustomOption }}
            onClickOptionEndButton={value => {
              openPopUp({
                url: editPaymentMethodLink(providerUrl.loginLink, window.location.href, value),
                target: 'editPaymentMethod',
                onCallback: () => {
                  refetch();
                  showSuccess({
                    type: 'toast',
                    message: t('pmSuccessfullyUpdated')
                  });
                }
              });
            }}
          />
          {selectedPaymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard &&
            (isMultiCCEnabled || isOrganizationAdmin) && (
              <Button
                style={{ gap: 4, marginTop: 4 }}
                variant="link"
                emphasis="700"
                startIcon={<Icon name="add" size="sm" />}
                onClick={() => {
                  openPopUp({
                    url: addPaymentMethodLink(providerUrl.loginLink, window.location.href),
                    target: 'editPaymentMethod',
                    onCallback: () => {
                      refetch();
                      showSuccess({
                        type: 'toast',
                        message: t('pmSuccessfullyAdded')
                      });
                    }
                  });
                }}
              >
                {t('addAPaymentMethod')}
              </Button>
            )}
        </>
      ) : (
        <Button
          emphasis="700"
          startIcon={<Icon size="sm" name="add" />}
          style={{ gap: '0.4rem', marginTop: 6 }}
          size="sm"
          onClick={() => {
            openPopUp({
              url: addPaymentMethodLink(providerUrl.loginLink, window.location.href),
              target: 'editPaymentMethod',
              onCallback: () => {
                refetch();
                showSuccess({
                  type: 'toast',
                  message: t('pmSuccessfullyAdded')
                });
              }
            });
          }}
        >
          {t('addAPaymentMethod')}
        </Button>
      )}
    </S.PaymentMethodInput>
  );
};

PaymentMethodInput.propTypes = {
  selectedPaymentMethod: PropTypes.object,
  setSelectedPaymentMethod: PropTypes.func,
  featureType: PropTypes.string,
  onTourButtonClick: PropTypes.func
};

export { PaymentMethodInput };
